import React from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import { InviteFriendCard } from 'components/portal/InviteFriendCard';
import { formatGroupedNumber } from 'lib/dateFormat';
import { useLearnerReferrals, type LearnerReferralStatus } from 'modules/learnerReferrals';
import { Users } from 'lucide-react';

const som = (value: number) => `${formatGroupedNumber(value)} so‘m`;

const STATUS_STYLE: Record<LearnerReferralStatus, string> = {
  pending: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  rewarded: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  reversed: 'bg-muted text-muted-foreground',
};

/** Settings → Takliflar: the invite link plus who joined through it and what it earned. */
export default function PortalReferrals() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useLearnerReferrals();
  const date = (iso?: string) => (iso ? new Date(iso).toLocaleDateString(i18n.language) : '');

  return (
    <div className="flex flex-col gap-5">
      <InviteFriendCard />

      <dl className="grid grid-cols-3 overflow-hidden rounded-lg border border-border bg-card">
        {[
          { label: t('referral.stat_invited'), value: data ? String(data.invited) : '—' },
          { label: t('referral.stat_rewarded'), value: data ? String(data.rewarded) : '—' },
          { label: t('referral.stat_earned'), value: data ? som(data.earnedSom) : '—' },
        ].map((stat, i) => (
          <div key={stat.label} className={`px-4 py-3.5 md:px-5 ${i > 0 ? 'border-l border-border' : ''}`}>
            <dt className="text-[12.5px] text-muted-foreground">{stat.label}</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground md:text-xl">{stat.value}</dd>
          </div>
        ))}
      </dl>

      <section aria-labelledby="referral-list-title" className="overflow-hidden rounded-lg border border-border bg-card">
        <h2 id="referral-list-title" className="px-4 pb-2.5 pt-3.5 text-[15px] font-semibold text-foreground md:px-5 md:pt-4">
          {t('referral.list_title')}
        </h2>
        <div className="border-t border-border">
          {isLoading ? (
            <div className="space-y-3 p-4 md:p-5" aria-hidden="true">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          ) : isError ? (
            <p className="p-4 text-sm text-destructive md:p-5">{t('referral.list_error')}</p>
          ) : !data?.items.length ? (
            <EmptyState icon={<Users className="h-5 w-5" />} title={t('referral.empty_title')} description={t('referral.empty_body')} />
          ) : (
            <ul className="divide-y divide-border">
              {data.items.map((item) => (
                <li key={item.id} className="flex items-center gap-3 px-4 py-3 md:px-5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{item.friendName}</p>
                    <p className="text-[12.5px] text-muted-foreground">{date(item.rewardedAt ?? item.createdAt)}</p>
                  </div>
                  {item.bonusSom > 0 && (
                    <span className="text-sm font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">+{som(item.bonusSom)}</span>
                  )}
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[item.status]}`}>
                    {t(`referral.status_${item.status}`)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
